import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Button, Row, Col, Modal, Badge, Spinner, Form, Alert, Tabs, Tab } from 'react-bootstrap';
import { Calendar, Tooltip, Tag, message } from 'antd';
import { HomeOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import {
  Instrument,
  createReservation,
  getInstrumentReservations,
  getInstrumentById,
  Reservation,
  getMyRecentReservations,
  isOccupyingReservation
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import dayjs, { Dayjs } from 'dayjs';
import ReservationList from '../components/ReservationList';
import InstrumentMaintenanceRecords from '../components/InstrumentMaintenanceRecords';
import MyInstrumentReservations from '../components/MyInstrumentReservations';
import { LimsNavIcon } from '../components/LimsNavIcon';
import { getEffectiveReservationDurationLimits } from '../utils/reservationDurationLimits';
import { resolveUploadAssetUrl, resolveStockItemImageSrc } from '../utils/resolveUploadAssetUrl';
import { DEFAULT_INSTRUMENT_IMAGE } from '../constants/defaultImages';
import { parseInstrumentUsageKbRef } from '../utils/instrumentUsageKb';
import {
  canCurrentUserReserveInstrument,
  formatInstrumentAccessDeniedMessage
} from '../utils/instrumentMemberAccess';
import '../styles/clinicalDetailModal.css';
import './InstrumentShowcase.css';

interface InstrumentShowcaseProps {
  data: Instrument[];
  loading: boolean;
  /** 为 true 且 loading 时，列表 Tab 显示整页加载（首屏无数据）；有数据刷新时不挡内容 */
  listInitialEmpty?: boolean;
  onRefresh: () => void;
  /** 全量仪器（用于统计卡）；缺省时用当前页的 data */
  statsInstruments?: Instrument[];
  /** 服务端总条数（分页），用于副标题提示 */
  totalCount?: number;
  /** 是否还有更多列表数据（下滑加载） */
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void | Promise<void>;
}

interface TimeSlot {
  time: string; // 开始时间 HH:mm
  endTime?: string; // 结束时间 HH:mm（30分钟段）
  available: boolean;
  disabled: boolean;
}

type ViewMode = 'list' | 'grid';

/** 分类下拉中「未分类」选项值，避免与真实分类名冲突 */
const SHOWCASE_UNCATEGORIZED_VALUE = '__uncategorized__';

function instrumentMatchesCatalogSearch(item: Instrument, rawQuery: string): boolean {
  const q = rawQuery.trim();
  if (!q) return true;
  const tokens = q.split(/\s+/).filter(Boolean);
  const hay = [
    item.name,
    item.model,
    item.manufacturer,
    item.location,
    item.category,
    item.description,
    item.responsiblePerson,
    item.usageInstructions
  ]
    .filter((x): x is string => typeof x === 'string' && x.trim().length > 0)
    .join(' ')
    .toLowerCase();
  return tokens.every((token) => hay.includes(token.toLowerCase()));
}

const InstrumentShowcase: React.FC<InstrumentShowcaseProps> = ({
  data,
  loading,
  listInitialEmpty = true,
  onRefresh,
  statsInstruments,
  totalCount,
  hasMore = false,
  loadingMore = false,
  onLoadMore
}) => {
  const navigate = useNavigate();
  const { token, user, teamDisplayName } = useAuth();

  const bookingAccessOpts = useMemo(
    () => ({
      role: user?.role,
      name: user?.name,
      username: user?.username
    }),
    [user?.role, user?.name, user?.username]
  );

  const assertCanBookInstrument = (instrument: Instrument | null | undefined): boolean => {
    if (!instrument) return false;
    if (canCurrentUserReserveInstrument(instrument, bookingAccessOpts)) return true;
    setAlertMsg(
      '您不在该仪器的可用成员名单中，暂时无法预约。如需开通权限，请联系仪器负责人或团队管理员。'
    );
    setAlertType('danger');
    return false;
  };

  const instrumentStats = useMemo(() => {
    const list = statsInstruments && statsInstruments.length > 0 ? statsInstruments : data;
    return {
      total: list.length,
      available: list.filter((i) => i.status === '可用').length,
      inUse: list.filter((i) => i.status === '使用中').length,
      down: list.filter((i) => ['维护中', '故障', '报废'].includes(i.status)).length
    };
  }, [statsInstruments, data]);
  const [activeTab, setActiveTab] = useState<string>('all');
  /** 仪器目录 Tab：''=全部分类，SHOWCASE_UNCATEGORIZED_VALUE=未分类，否则为具体分类名 */
  const [catalogCategoryFilter, setCatalogCategoryFilter] = useState<string>('');
  const [catalogSearchQuery, setCatalogSearchQuery] = useState<string>('');
  const [recentReservations, setRecentReservations] = useState<Reservation[]>([]);
  const [recentReservationsLoading, setRecentReservationsLoading] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('instrumentViewMode') as ViewMode;
    if (saved && (saved === 'list' || saved === 'grid')) {
      return saved;
    }
    return 'grid';
  });
  const [showDetail, setShowDetail] = useState(false);
  const [detailItem, setDetailItem] = useState<Instrument | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const detailFetchSeq = useRef(0);
  const infiniteSentinelRef = useRef<HTMLDivElement | null>(null);
  const [showBooking, setShowBooking] = useState(false);
  const [bookingInstrument, setBookingInstrument] = useState<Instrument | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedStartTime, setSelectedStartTime] = useState<string>('');
  const [selectedEndTime, setSelectedEndTime] = useState<string>('');
  const [purpose, setPurpose] = useState<string>('');
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [alertMsg, setAlertMsg] = useState('');
  const [alertType, setAlertType] = useState<'success' | 'danger'>('success');
  const [bookingLoading, setBookingLoading] = useState(false);
  const [showBookingConfirm, setShowBookingConfirm] = useState(false);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState<Dayjs | null>(null);
  const [calendarMode, setCalendarMode] = useState<'month' | 'year'>('month');
  const [selectedInstrumentForList, setSelectedInstrumentForList] = useState<Instrument | null>(null);
  const [selectedInstrumentForMaintenance, setSelectedInstrumentForMaintenance] = useState<Instrument | null>(null);
  const [showMyReservationsModal, setShowMyReservationsModal] = useState(false);
  const [myReservationsModalKey, setMyReservationsModalKey] = useState(0);
  const [lastClickedSlotKey, setLastClickedSlotKey] = useState<string>('');
  const [sameSlotClickCount, setSameSlotClickCount] = useState<number>(0);
  const bookingInstrumentFetchTargetIdRef = useRef<string | null>(null);

  const bookingDurationLimits = useMemo(
    () => getEffectiveReservationDurationLimits(bookingInstrument),
    [bookingInstrument]
  );

  /** 仅统计会占用时段的预约（已取消/已拒绝/已完成不计入） */
  const occupyingReservations = useMemo(
    () => reservations.filter(isOccupyingReservation),
    [reservations]
  );

  // 切换视图模式并保存到localStorage
  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    localStorage.setItem('instrumentViewMode', mode);
  };

  // 获取用户近期预约记录
  useEffect(() => {
    if (token && user) {
      fetchRecentReservations();
    }
  }, [token, user]);

  const fetchRecentReservations = async () => {
    setRecentReservationsLoading(true);
    try {
      const response = await getMyRecentReservations();
      if (response.success) {
        setRecentReservations(response.data || []);
      } else {
        console.warn('获取近期预约记录失败:', response.error);
        setRecentReservations([]);
      }
    } catch (error) {
      console.error('获取近期预约记录失败:', error);
      setRecentReservations([]);
    } finally {
      setRecentReservationsLoading(false);
    }
  };

  const handleShowDetail = (item: Instrument) => {
    const seq = ++detailFetchSeq.current;
    setDetailItem(item);
    setShowDetail(true);
    setDetailLoading(true);
    const id = item._id;
    if (!id) {
      setDetailLoading(false);
      return;
    }
    void (async () => {
      try {
        const res = await getInstrumentById(id);
        if (detailFetchSeq.current !== seq) return;
        if (res.success && res.data) {
          setDetailItem(res.data);
        } else {
          message.warning(res.error || '完整详情加载失败，当前为列表摘要');
        }
      } catch {
        if (detailFetchSeq.current === seq) {
          message.error('网络错误，完整详情加载失败');
        }
      } finally {
        if (detailFetchSeq.current === seq) {
          setDetailLoading(false);
        }
      }
    })();
  };

  // 显示预约列表
  const handleShowReservationList = (item: Instrument) => {
    setSelectedInstrumentForList(item);
  };

  const handleShowMaintenanceRecords = (item: Instrument) => {
    if (!token) {
      message.warning('请先登录后查看维护记录');
      return;
    }
    setSelectedInstrumentForMaintenance(item);
  };

  // 预约相关函数（列表无 reservationRules 投影，需拉详情才能按规则校验与灰选时段）
  const handleBooking = (item: Instrument) => {
    if (!token) {
      setAlertMsg('请先登录');
      setAlertType('danger');
      return;
    }
    const idStr = item._id ? String(item._id) : null;
    bookingInstrumentFetchTargetIdRef.current = idStr;
    setBookingInstrument(item);
    setSelectedDate('');
    setSelectedStartTime('');
    setSelectedEndTime('');
    setPurpose('');
    setTimeSlots([]);
    setSelectedCalendarDate(null);
    setShowBookingConfirm(false);
    setShowBooking(true);
    if (item._id) {
      fetchReservations(item._id);
      void getInstrumentById(item._id).then((response) => {
        if (!response.success || !response.data) return;
        if (bookingInstrumentFetchTargetIdRef.current !== String(item._id)) return;
        setBookingInstrument(response.data);
        if (!canCurrentUserReserveInstrument(response.data, bookingAccessOpts)) {
          setAlertMsg(
            '您不在该仪器的可用成员名单中，暂时无法预约。如需开通权限，请联系仪器负责人或团队管理员。'
          );
          setAlertType('danger');
        }
      });
    }
  };

  // 获取仪器的预约数据
  const fetchReservations = async (instrumentId: string) => {
    try {
      const response = await getInstrumentReservations(instrumentId);
      if (response.success) {
        setReservations(response.data || []);
      } else {
        console.warn('获取预约数据失败:', response.error);
        setReservations([]);
      }
    } catch (error) {
      console.error('获取预约数据失败:', error);
      setReservations([]);
    }
  };

  // 生成预约日历 - 根据仪器规则生成精确时间段（30分钟为最小粒度，按钮显示为最短时长段）
  const generateTimeSlots = (instrument: Instrument, date: string) => {
    const rules = instrument.reservationRules;
    const selectedDate = new Date(date);
    const dayOfWeek = selectedDate.getDay();
    
    // 如果没有预约规则，使用默认规则：每天07:00-23:00
    if (!rules || !rules.availableTimeSlots) {
      const slots: TimeSlot[] = [];
      const startTime = parseTimeToMinutes('07:00');
      const endTime = parseTimeToMinutes('23:00');
      const minDuration = 30; // 默认最短时长
      const interval = 30; // 选择步长
      
      for (let minutes = startTime; minutes + minDuration <= endTime; minutes += interval) {
        const timeStr = formatMinutesToTime(minutes);
        const endStr = formatMinutesToTime(minutes + minDuration);
        slots.push({
          time: timeStr,
          endTime: endStr,
          available: true,
          disabled: false
        });
      }
      return slots;
    }

    const timeSlot = rules.availableTimeSlots.find(slot => slot.dayOfWeek === dayOfWeek);
    
    if (!timeSlot || !timeSlot.isEnabled) {
      // 若当日未配置，则回退到默认 07:00-23:00
      const slots: TimeSlot[] = [];
      const startTime = parseTimeToMinutes('07:00');
      const endTime = parseTimeToMinutes('23:00');
      const minDuration = 30;
      const interval = 30;
      for (let minutes = startTime; minutes + minDuration <= endTime; minutes += interval) {
        const timeStr = formatMinutesToTime(minutes);
        const endStr = formatMinutesToTime(minutes + minDuration);
        slots.push({ time: timeStr, endTime: endStr, available: true, disabled: false });
      }
      return slots;
    }

    const slots: TimeSlot[] = [];
    const startTime = parseTimeToMinutes(timeSlot.startTime);
    const endTime = parseTimeToMinutes(timeSlot.endTime);
    
    const minDuration = Math.max(15, Math.min(24 * 60, rules.minDuration || 30));
    const interval = 30; // 以30分钟为粒度
    
    for (let minutes = startTime; minutes + minDuration <= endTime; minutes += interval) {
      const timeStr = formatMinutesToTime(minutes);
      const endStr = formatMinutesToTime(minutes + minDuration);
      slots.push({
        time: timeStr,
        endTime: endStr,
        available: true,
        disabled: false
      });
    }
    
    return slots;
  };

  const parseTimeToMinutes = (timeStr: string): number => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const formatMinutesToTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  const handleTimeSlotClick = (slot: TimeSlot) => {
    if (!slot.endTime) return;
    const slotKey = `${slot.time}-${slot.endTime}`;
    const isSameAsLast = slotKey === lastClickedSlotKey;
    const nextCount = isSameAsLast ? sameSlotClickCount + 1 : 1;
    setLastClickedSlotKey(slotKey);
    setSameSlotClickCount(nextCount);

    // 双击同一段：固定为该单段；第三次点击同一段：清空选择
    if (nextCount === 3) {
      setSelectedStartTime('');
      setSelectedEndTime('');
      setLastClickedSlotKey('');
      setSameSlotClickCount(0);
      return;
    }
    if (nextCount === 2) {
      setSelectedStartTime(slot.time);
      setSelectedEndTime(slot.endTime);
      return;
    }
    if (!selectedStartTime) {
      setSelectedStartTime(slot.time);
      setSelectedEndTime(slot.endTime);
      return;
    }
    const startMinutes = parseTimeToMinutes(selectedStartTime);
    const candidateEndMinutes = parseTimeToMinutes(slot.endTime);
    const candidateStartMinutes = parseTimeToMinutes(slot.time);
    // 若点击的段在当前开始之前或与当前结束相同，重置为新的起始段
    if (candidateStartMinutes <= startMinutes) {
      setSelectedStartTime(slot.time);
      setSelectedEndTime(slot.endTime);
      return;
    }
    // 扩展结束时间
    if (candidateEndMinutes > startMinutes) {
      setSelectedEndTime(slot.endTime);
    }
  };

  const validateBooking = () => {
    if (!selectedDate || !selectedStartTime || !selectedEndTime || !purpose.trim()) {
      setAlertMsg('请填写完整的预约信息');
      setAlertType('danger');
      return false;
    }

    if (!bookingInstrument) return false;

    const { minDuration, maxDuration } = bookingDurationLimits;
    const startMinutes = parseTimeToMinutes(selectedStartTime);
    const endMinutes = parseTimeToMinutes(selectedEndTime);
    const duration = endMinutes - startMinutes;

    if (duration < minDuration) {
      setAlertMsg(`预约时间不能少于${minDuration}分钟`);
      setAlertType('danger');
      return false;
    }

    if (duration > maxDuration) {
      setAlertMsg(`预约时间不能超过${maxDuration}分钟`);
      setAlertType('danger');
      return false;
    }

    return true;
  };

  const handleCloseBookingModal = () => {
    bookingInstrumentFetchTargetIdRef.current = null;
    setShowBookingConfirm(false);
    setShowBooking(false);
    setAlertMsg('');
  };

  /** 校验通过后打开确认弹窗，避免误触直接占用时段 */
  const handleOpenBookingConfirm = () => {
    if (!validateBooking() || !bookingInstrument) return;
    if (!assertCanBookInstrument(bookingInstrument)) return;
    setShowBookingConfirm(true);
  };

  const handleConfirmSubmitBooking = async () => {
    if (!bookingInstrument || !selectedDate || !selectedStartTime || !selectedEndTime) return;

    setBookingLoading(true);
    try {
      const startDateTime = `${selectedDate}T${selectedStartTime}:00`;
      const endDateTime = `${selectedDate}T${selectedEndTime}:00`;

      const hasResponsibleApprover = Boolean(bookingInstrument.responsiblePerson && bookingInstrument.responsiblePerson.trim());
      const requiresApprovalEnabled = bookingInstrument.reservationRules?.requiresApproval !== false;
      const needsResponsibleApproval = hasResponsibleApprover && requiresApprovalEnabled;
      const response = await createReservation({
        instrumentId: bookingInstrument._id!,
        startTime: startDateTime,
        endTime: endDateTime,
        purpose: purpose.trim(),
        status: needsResponsibleApproval ? '待审核' : '已批准'
      });

      if (response.success) {
        setShowBookingConfirm(false);
        setAlertMsg(needsResponsibleApproval ? '预约提交成功，等待设备负责人审批' : '预约成功（该设备无需审批，已自动通过）');
        setAlertType('success');
        if (token && user) {
          fetchRecentReservations();
        }
        setTimeout(() => {
          setShowBooking(false);
          setAlertMsg('');
        }, 2000);
      } else {
        setAlertMsg(
          formatInstrumentAccessDeniedMessage(
            typeof response.error === 'string' ? response.error : undefined,
            response.contacts
          ) || '预约失败'
        );
        setAlertType('danger');
      }
    } catch {
      setAlertMsg('预约失败，请稍后重试');
      setAlertType('danger');
    } finally {
      setBookingLoading(false);
    }
  };

  // 日历相关函数
  const handleCalendarDateSelect = (date: Dayjs) => {
    setSelectedCalendarDate(date);
    const dateStr = date.format('YYYY-MM-DD');
    setSelectedDate(dateStr);
    
    if (bookingInstrument) {
      const slots = generateTimeSlots(bookingInstrument, dateStr);
      setTimeSlots(slots);
    }
  };

  const getReservationsForDate = (date: Dayjs) => {
    const dateStr = date.format('YYYY-MM-DD');
    return occupyingReservations.filter(reservation => {
      const startDate = dayjs(reservation.startTime).format('YYYY-MM-DD');
      return startDate === dateStr;
    });
  };

  // 已不再需要时间点冲突判断函数（基于整段容量校验）

  /** 仅渲染「日期内容区」：序号由 antd Calendar 的 date-value 统一绘制，此处再画会重复 */
  const renderCalendarCell = (date: Dayjs, info: { type: string }) => {
    if (info.type !== 'date') {
      return null;
    }
    const dayReservations = getReservationsForDate(date);
    if (dayReservations.length === 0) {
      return null;
    }
    return (
      <div style={{ fontSize: '10px', marginTop: '2px' }}>
        <Tag color="blue">{dayReservations.length}个预约</Tag>
      </div>
    );
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case '可用': return 'success';
      case '使用中': return 'warning';
      case '维护中': return 'danger';
      case '故障': return 'dark';
      case '报废': return 'secondary';
      default: return 'secondary';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case '可用': return '可用';
      case '使用中': return '使用中';
      case '维护中': return '维护中';
      case '故障': return '故障';
      case '报废': return '报废';
      default: return status;
    }
  };

  const cardDescription = (item: Instrument) => {
    if (item.description?.trim()) return item.description.trim();
    const parts = [item.model, item.manufacturer].filter(Boolean);
    return parts.length ? parts.join(' · ') : '实验室共享仪器设备，支持按需预约。';
  };

  /** 详情弹窗顶栏状态（勿用卡片用的 is-pill，其为 absolute 定位） */
  const detailModalStatusBadge = (status: string) => {
    const text = getStatusText(status);
    if (status === '可用') {
      return (
        <span className="ins-detail-modal__pill ins-detail-modal__pill--avail">
          <span className="ins-detail-modal__pill-dot" />
          {text}
        </span>
      );
    }
    if (status === '使用中') {
      return (
        <span className="ins-detail-modal__pill ins-detail-modal__pill--busy">
          <span className="ins-detail-modal__pill-dot" />
          {text}
        </span>
      );
    }
    return (
      <span className="ins-detail-modal__pill ins-detail-modal__pill--muted">
        <LimsNavIcon name="warning" sizePx={14} style={{ fontVariationSettings: "'FILL' 1" }} />
        {text}
      </span>
    );
  };

  const statusPill = (status: string) => {
    const text = getStatusText(status);
    if (status === '可用') {
      return (
        <span className="is-pill is-pill--avail">
          <span className="is-pill-dot" />
          {text}
        </span>
      );
    }
    if (status === '使用中') {
      return (
        <span className="is-pill is-pill--busy">
          <span className="is-pill-dot" />
          {text}
        </span>
      );
    }
    return (
      <span className="is-pill is-pill--warn">
        <LimsNavIcon name="warning" sizePx={14} style={{ fontVariationSettings: "'FILL' 1" }} />
        {text}
      </span>
    );
  };

  const cardPrimaryAction = (item: Instrument, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (item.status === '可用') {
      handleBooking(item);
      return;
    }
    if (item.status === '使用中') {
      handleBooking(item);
      return;
    }
    message.info('维护/故障期间可联系管理员；开放预约后将通知成员。');
  };

  // 检测是否为移动端竖屏
  const isMobilePortrait = () => {
    return window.innerWidth <= 768 && window.innerHeight > window.innerWidth;
  };

  const isMobile = () => window.innerWidth <= 768;

  // 获取所有分类
  const getAllCategories = () => {
    const categories = new Set<string>();
    data.forEach(instrument => {
      if (instrument.category) {
        categories.add(instrument.category);
      }
    });
    return Array.from(categories).sort();
  };

  // 获取近期预约的仪器（去重）
  const getRecentReservedInstruments = (): Instrument[] => {
    const instrumentMap = new Map<string, Instrument>();
    recentReservations.forEach(reservation => {
      if (typeof reservation.instrumentId === 'object' && reservation.instrumentId) {
        const instrument = reservation.instrumentId as Instrument;
        if (instrument._id && !instrumentMap.has(instrument._id)) {
          instrumentMap.set(instrument._id, instrument);
        }
      }
    });
    return Array.from(instrumentMap.values());
  };

  // 根据当前标签页与目录筛选（分类 + 关键词）得到列表
  const displayedInstruments = useMemo(() => {
    if (activeTab === 'recent') {
      if (!token || !user) return [];
      return getRecentReservedInstruments();
    }
    let filtered = [...data];
    if (catalogCategoryFilter === SHOWCASE_UNCATEGORIZED_VALUE) {
      filtered = filtered.filter((item) => !item.category || item.category.trim() === '');
    } else if (catalogCategoryFilter) {
      filtered = filtered.filter((item) => item.category === catalogCategoryFilter);
    }
    const q = catalogSearchQuery.trim();
    if (q) {
      filtered = filtered.filter((item) => instrumentMatchesCatalogSearch(item, q));
    }
    return filtered;
  }, [activeTab, data, token, user, recentReservations, catalogCategoryFilter, catalogSearchQuery]);

  // 渲染仪器卡片（网格视图 — stitch / LabMind）
  const renderInstrumentCard = (item: Instrument, idx: number) => {
    const maint = item.status === '维护中' || item.status === '故障' || item.status === '报废';
    return (
      <div
        key={item._id || idx}
        className={`is-card${maint ? ' is-muted' : ''}`}
        onClick={() => handleShowDetail(item)}
        onKeyDown={(e) => e.key === 'Enter' && handleShowDetail(item)}
        role="button"
        tabIndex={0}
      >
        <div className="is-card-media">
          <img src={resolveStockItemImageSrc(item.image, DEFAULT_INSTRUMENT_IMAGE)} alt="" loading="lazy" />
          {statusPill(item.status)}
        </div>
        <div className="is-card-body">
          <h3 className="is-card-title">{item.name}</h3>
          <div className="is-card-location">
            <span className="is-meta-label">位置</span>
            <div className="is-meta-value">{item.location || '—'}</div>
          </div>
          <p className="is-card-desc">{cardDescription(item)}</p>
          <div className="is-card-foot">
            <div>
              <span className="is-meta-label">负责人</span>
              <div className="is-meta-value">{item.responsiblePerson || '—'}</div>
            </div>
            <div className="is-card-actions">
              {item.status === '可用' && (
                <button
                  type="button"
                  className="is-btn-cta is-btn-cta--primary"
                  onClick={(e) => cardPrimaryAction(item, e)}
                >
                  立即预约
                </button>
              )}
              {item.status === '使用中' && (
                <button
                  type="button"
                  className="is-btn-cta is-btn-cta--queue"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleBooking(item);
                  }}
                >
                  排队预约
                </button>
              )}
              {(item.status === '维护中' || item.status === '故障' || item.status === '报废') && (
                <button type="button" className="is-btn-cta is-btn-cta--ghost" onClick={(e) => cardPrimaryAction(item, e)}>
                  提醒我
                </button>
              )}
              <button
                type="button"
                className="is-linkish"
                onClick={(e) => {
                  e.stopPropagation();
                  handleShowReservationList(item);
                }}
              >
                预约列表
              </button>
              <button
                type="button"
                className="is-linkish"
                onClick={(e) => {
                  e.stopPropagation();
                  handleShowMaintenanceRecords(item);
                }}
              >
                维护记录
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 渲染仪器列表项（列表视图）
  const renderInstrumentListItem = (item: Instrument, idx: number) => (
    <div key={item._id || idx} className="is-list-row" onClick={() => handleShowDetail(item)} role="presentation">
      <div className="is-list-thumb">
        <img src={resolveStockItemImageSrc(item.image, DEFAULT_INSTRUMENT_IMAGE)} alt="" loading="lazy" />
        <div style={{ position: 'absolute', top: 8, right: 8, transform: 'scale(0.92)', transformOrigin: 'top right' }}>
          {statusPill(item.status)}
        </div>
      </div>
      <div className="is-list-main">
        <h3 className="is-card-title" style={{ marginBottom: 6 }}>
          {item.name}
        </h3>
        <p className="is-card-desc" style={{ WebkitLineClamp: 2, marginBottom: 8 }}>
          {cardDescription(item)}
        </p>
        <div style={{ fontSize: 13, color: 'var(--is-on-variant)' }}>
          <span style={{ marginRight: 16 }}>型号 {item.model || '—'}</span>
          <span>位置 {item.location || '—'}</span>
        </div>
      </div>
      <div className="is-list-actions">
        <button
          type="button"
          className="is-btn-cta is-btn-cta--ghost"
          onClick={(e) => {
            e.stopPropagation();
            handleShowDetail(item);
          }}
        >
          详情
        </button>
        {item.status === '可用' && (
          <button
            type="button"
            className="is-btn-cta is-btn-cta--primary"
            onClick={(e) => {
              e.stopPropagation();
              handleBooking(item);
            }}
          >
            预约
          </button>
        )}
        {item.status === '使用中' && (
          <button
            type="button"
            className="is-btn-cta is-btn-cta--queue"
            onClick={(e) => {
              e.stopPropagation();
              handleBooking(item);
            }}
          >
            排队预约
          </button>
        )}
        <button
          type="button"
          className="is-btn-cta is-btn-cta--ghost"
          onClick={(e) => {
            e.stopPropagation();
            handleShowReservationList(item);
          }}
        >
          预约列表
        </button>
        <button
          type="button"
          className="is-btn-cta is-btn-cta--ghost"
          onClick={(e) => {
            e.stopPropagation();
            handleShowMaintenanceRecords(item);
          }}
        >
          维护记录
        </button>
      </div>
    </div>
  );

  const tabBlockingLoad = loading && listInitialEmpty;

  const showInfiniteSentinel = Boolean(
    onLoadMore && hasMore && !tabBlockingLoad && activeTab !== 'recent' && data.length > 0
  );

  useEffect(() => {
    const el = infiniteSentinelRef.current;
    if (!el || !showInfiniteSentinel || !onLoadMore) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const hit = entries.some((e) => e.isIntersecting);
        if (!hit || loadingMore || tabBlockingLoad) return;
        void onLoadMore();
      },
      { root: null, rootMargin: '320px 0px', threshold: 0 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [onLoadMore, hasMore, tabBlockingLoad, loadingMore, activeTab, data.length, showInfiniteSentinel]);

  const renderViewToggle = (inline: boolean = false) => {
    const mobile = isMobile();
    return (
      <div
        className="is-view-toggle"
        style={{ justifyContent: inline ? 'flex-end' : mobile ? 'center' : 'flex-end' }}
      >
        <span>视图</span>
        <button
          type="button"
          className={`is-chip-toggle${viewMode === 'grid' ? ' is-active' : ''}`}
          onClick={() => handleViewModeChange('grid')}
        >
          网格
        </button>
        <button
          type="button"
          className={`is-chip-toggle${viewMode === 'list' ? ' is-active' : ''}`}
          onClick={() => handleViewModeChange('list')}
        >
          列表
        </button>
      </div>
    );
  };

  const mobilePortrait = isMobilePortrait();

  return (
    <div className="is-showcase-lims">
      <nav className="mm-crumb" aria-label="breadcrumb">
        <HomeOutlined />
        <span>首页</span>
        <span className="mm-crumb-sep">/</span>
        <span>仪器</span>
      </nav>

      <header className="mm-page-head">
        <div>
          <h1 className="mm-title">仪器展示</h1>
          <p className="mm-subtitle">
            {teamDisplayName || '实验室团队'} · 设备实时状态与预约目录。
            {totalCount != null && totalCount > 0 && (
              <span className="d-block mt-2" style={{ fontSize: 13, fontWeight: 500 }}>
                全库共 {totalCount} 台登记设备
              </span>
            )}
          </p>
        </div>
        <button
          type="button"
          className="mm-btn-primary"
          disabled={!token || !user}
          onClick={() => {
            if (!token || !user) {
              message.info('请先登录后查看我的预约');
              navigate('/');
              return;
            }
            setMyReservationsModalKey((k) => k + 1);
            setShowMyReservationsModal(true);
          }}
        >
          <LimsNavIcon name="calendar_today" sizePx={18} />
          我的预约
        </button>
      </header>

      <section className="mm-stats mm-stats--quad" aria-label="仪器概览">
        <article className="mm-stat">
          <span className="mm-stat-label">设备总数</span>
          <span className="mm-stat-value">{instrumentStats.total}</span>
          <div className="mm-stat-foot">
            <LimsNavIcon name="biotech" sizePx={18} />
            <span>登记在册</span>
          </div>
          <LimsNavIcon name="biotech" sizePx={120} className="mm-stat-deco" />
        </article>
        <article className="mm-stat">
          <span className="mm-stat-label">当前可用</span>
          <span className="mm-stat-value">{instrumentStats.available}</span>
          <div className="mm-stat-foot">
            <LimsNavIcon name="check_circle" sizePx={18} />
            <span>可预约</span>
          </div>
          <LimsNavIcon name="check_circle" sizePx={120} className="mm-stat-deco" />
        </article>
        <article className="mm-stat">
          <span className="mm-stat-label">使用中</span>
          <span className="mm-stat-value">{instrumentStats.inUse}</span>
          <div className="mm-stat-foot">
            <LimsNavIcon name="schedule" sizePx={18} />
            <span>占用运行</span>
          </div>
          <LimsNavIcon name="schedule" sizePx={120} className="mm-stat-deco" />
        </article>
        <article className="mm-stat">
          <span className="mm-stat-label">维护 / 停用</span>
          <span className={`mm-stat-value${instrumentStats.down > 0 ? ' mm-stat-value--alert' : ''}`}>
            {instrumentStats.down}
          </span>
          <div className="mm-stat-foot">
            <LimsNavIcon name="build" sizePx={18} />
            <span>含维护、故障、报废</span>
          </div>
          <LimsNavIcon name="build" sizePx={120} className="mm-stat-deco" />
        </article>
      </section>

      <div
        style={{
          position: 'relative',
          marginBottom: mobilePortrait ? '12px' : '8px'
        }}
      >
        {!mobilePortrait && (
          <div style={{ position: 'absolute', right: 0, top: -6, zIndex: 10 }}>
            {renderViewToggle(true)}
          </div>
        )}
        {mobilePortrait && (
          <div style={{ marginBottom: '12px' }}>
            {renderViewToggle(false)}
          </div>
        )}
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k || 'all')}
          className={`is-showcase-tabs ${mobilePortrait ? 'mb-3' : 'mb-4 pe-5'}`}
        >
        {token && user && (
          <Tab eventKey="recent" title="近期预约">
            {recentReservationsLoading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <div className="mt-3">加载中...</div>
              </div>
            ) : displayedInstruments.length === 0 ? (
              <div className="text-center py-5">
                <div className="mb-3">
                  <i className="bi bi-inbox" style={{ fontSize: '3rem', color: '#ccc' }}></i>
                </div>
                <h5 className="text-muted">近期暂无预约记录</h5>
                <p className="text-muted">您近14天内还没有预约过任何仪器</p>
                <p className="text-muted">快去浏览并预约您需要的仪器吧！</p>
                <Button variant="primary" onClick={() => setActiveTab('all')}>
                  浏览所有仪器
                </Button>
              </div>
            ) : viewMode === 'list' ? (
              <div className="is-list">
                {displayedInstruments.map((item: Instrument, idx: number) => renderInstrumentListItem(item, idx))}
              </div>
            ) : (
              <div className="is-grid">
                {displayedInstruments.map((item: Instrument, idx: number) => renderInstrumentCard(item, idx))}
              </div>
            )}
          </Tab>
        )}
        <Tab eventKey="all" title="所有仪器">
          <div className="is-catalog-toolbar">
            <div className="is-catalog-toolbar-inner">
              <Form.Select
                size="sm"
                className="is-catalog-category-select"
                aria-label="按分类筛选"
                value={catalogCategoryFilter}
                onChange={(e) => setCatalogCategoryFilter(e.target.value)}
              >
                <option value="">全部分类</option>
                <option value={SHOWCASE_UNCATEGORIZED_VALUE}>未分类</option>
                {getAllCategories().map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </Form.Select>
              <div className="is-catalog-search">
                <LimsNavIcon name="search" sizePx={18} className="is-catalog-search-icon" />
                <input
                  type="search"
                  className="is-catalog-search-input"
                  placeholder="名称、型号、位置、厂家…"
                  value={catalogSearchQuery}
                  onChange={(e) => setCatalogSearchQuery(e.target.value)}
                  aria-label="搜索仪器"
                />
              </div>
            </div>
          </div>
          {tabBlockingLoad ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <div className="mt-3">加载中...</div>
            </div>
          ) : data.length === 0 ? (
            <div className="text-center py-5">
              <div>暂无仪器数据</div>
              <Button variant="primary" onClick={onRefresh} className="mt-2">
                刷新
              </Button>
            </div>
          ) : displayedInstruments.length === 0 ? (
            <div className="text-center py-5">
              <div className="text-muted">没有符合条件的仪器</div>
              <p className="text-muted small mt-2">可调整分类或修改搜索关键词</p>
              <Button
                variant="outline-primary"
                size="sm"
                className="mt-2 me-2"
                onClick={() => {
                  setCatalogCategoryFilter('');
                  setCatalogSearchQuery('');
                }}
              >
                清除筛选
              </Button>
              <Button variant="primary" size="sm" className="mt-2" onClick={onRefresh}>
                刷新列表
              </Button>
            </div>
          ) : viewMode === 'list' ? (
            <div className="is-list" style={{ paddingBottom: mobilePortrait ? '16px' : '0' }}>
              {displayedInstruments.map((item: Instrument, idx: number) => renderInstrumentListItem(item, idx))}
            </div>
          ) : (
            <div className="is-grid" style={{ marginBottom: mobilePortrait ? '16px' : '0' }}>
              {displayedInstruments.map((item: Instrument, idx: number) => renderInstrumentCard(item, idx))}
            </div>
          )}
        </Tab>
        </Tabs>
      </div>

      {/* 详情弹窗 — stitch (12) / Clinical Curator */}
      <Modal
        show={showDetail}
        onHide={() => {
          detailFetchSeq.current += 1;
          setDetailLoading(false);
          setShowDetail(false);
        }}
        centered
        fullscreen={mobilePortrait ? true : undefined}
        dialogClassName="ins-detail-modal__dialog"
        contentClassName="ins-detail-modal__content"
        backdropClassName="ins-detail-modal__backdrop"
        style={mobilePortrait ? { margin: 0 } : undefined}
      >
        {detailItem && (
          <Modal.Body className="ins-detail-modal__body">
            <header className="ins-detail-modal__header">
              <div className="ins-detail-modal__header-left">
                <div className="ins-detail-modal__header-icon" aria-hidden>
                  <LimsNavIcon name="biotech" sizePx={26} />
                </div>
                <div>
                  <h2 className="ins-detail-modal__title">仪器详情</h2>
                  <div className="ins-detail-modal__subtitle-row">
                    <span className="ins-detail-modal__subtitle-dot" aria-hidden />
                    <span className="ins-detail-modal__subtitle">
                      系统编号：{detailItem._id ? String(detailItem._id) : '—'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="ins-detail-modal__header-right">
                <div className="ins-detail-modal__status-wrap">{detailModalStatusBadge(detailItem.status)}</div>
                <button
                  type="button"
                  className="ins-detail-modal__close"
                  onClick={() => {
                    detailFetchSeq.current += 1;
                    setDetailLoading(false);
                    setShowDetail(false);
                  }}
                  aria-label="关闭"
                >
                  <LimsNavIcon name="close" sizePx={22} />
                </button>
              </div>
            </header>

            <div className="ins-detail-modal__scroll">
              <div style={{ opacity: detailLoading ? 0.55 : 1, transition: 'opacity 0.2s' }}>
                <div className="ins-detail-modal__hero">
                  <div className="ins-detail-modal__hero-img-wrap">
                    <img
                      src={
                        resolveUploadAssetUrl(detailItem.image) ||
                        detailItem.image ||
                        DEFAULT_INSTRUMENT_IMAGE
                      }
                      alt=""
                      className="ins-detail-modal__hero-img"
                      loading="lazy"
                    />
                  </div>
                  <div className="ins-detail-modal__hero-copy">
                    <h3 className="ins-detail-modal__section-title">描述</h3>
                    <p className="ins-detail-modal__desc">
                      {detailItem.description?.trim() || '暂无描述'}
                    </p>
                    <div className="ins-detail-modal__usage-block">
                      <h3 className="ins-detail-modal__usage-kicker">使用说明</h3>
                      {(() => {
                        const kb = parseInstrumentUsageKbRef(detailItem);
                        if (kb) {
                          return (
                            <div className="ins-detail-modal__usage-kb">
                              {kb.title?.trim() ? (
                                <p className="ins-detail-modal__usage-kb-title">{kb.title.trim()}</p>
                              ) : null}
                              <Link
                                to={`/knowledge-base/${kb.id}`}
                                className="ins-detail-modal__usage-kb-link"
                              >
                                在知识库中查看完整使用说明
                              </Link>
                            </div>
                          );
                        }
                        return (
                          <p className="ins-detail-modal__muted">
                            该仪器未添加使用说明，请联系管理员。
                          </p>
                        );
                      })()}
                    </div>
                  </div>
                </div>

                <section className="ins-detail-modal__section">
                  <div className="ins-detail-modal__section-head">
                    <LimsNavIcon name="info" sizePx={20} className="ins-detail-modal__section-icon" />
                    <h3 className="ins-detail-modal__section-title-lg">基本信息</h3>
                  </div>
                  <div className="ins-detail-modal__bento">
                    {[
                      { label: '仪器名称', value: detailItem.name || '—' },
                      { label: '分类', value: detailItem.category?.trim() || '—' },
                      { label: '型号', value: detailItem.model?.trim() || '—' },
                      { label: '制造商', value: detailItem.manufacturer?.trim() || '—' },
                      { label: '位置', value: detailItem.location?.trim() || '—' },
                      { label: '负责人', value: detailItem.responsiblePerson?.trim() || '—' }
                    ].map((cell) => (
                      <div key={cell.label} className="ins-detail-modal__bento-cell">
                        <span className="ins-detail-modal__field-label">{cell.label}</span>
                        <p className="ins-detail-modal__field-value">{cell.value}</p>
                      </div>
                    ))}
                  </div>
                </section>

                <div className="ins-detail-modal__split">
                  <section className="ins-detail-modal__panel">
                    <h3 className="ins-detail-modal__section-title-lg">生命周期</h3>
                    <div className="ins-detail-modal__panel-card">
                      <div className="ins-detail-modal__panel-row">
                        <div className="ins-detail-modal__panel-row-left">
                          <LimsNavIcon name="calendar_today" sizePx={20} className="ins-detail-modal__panel-icon" />
                          <span>购买日期</span>
                        </div>
                        <span className="ins-detail-modal__panel-value">
                          {detailItem.purchaseDate
                            ? new Date(detailItem.purchaseDate).toLocaleDateString('zh-CN')
                            : '—'}
                        </span>
                      </div>
                      <div className="ins-detail-modal__panel-divider" />
                      <div className="ins-detail-modal__panel-row">
                        <div className="ins-detail-modal__panel-row-left">
                          <LimsNavIcon name="verified_user" sizePx={20} className="ins-detail-modal__panel-icon" />
                          <span>保修到期</span>
                        </div>
                        <span className="ins-detail-modal__panel-value">
                          {detailItem.warrantyExpiry
                            ? new Date(detailItem.warrantyExpiry).toLocaleDateString('zh-CN')
                            : '—'}
                        </span>
                      </div>
                    </div>
                  </section>
                  <section className="ins-detail-modal__panel">
                    <h3 className="ins-detail-modal__section-title-lg">预约规则</h3>
                    <div className="ins-detail-modal__panel-card">
                      <div className="ins-detail-modal__booking-grid">
                        <div>
                          <span className="ins-detail-modal__field-label">单次最长</span>
                          <div className="ins-detail-modal__booking-num">
                            <span>{detailItem.reservationRules?.maxDuration ?? 240}</span>
                            <span className="ins-detail-modal__booking-unit">分钟</span>
                          </div>
                        </div>
                        <div>
                          <span className="ins-detail-modal__field-label">单次最短</span>
                          <div className="ins-detail-modal__booking-num">
                            <span>{detailItem.reservationRules?.minDuration ?? 30}</span>
                            <span className="ins-detail-modal__booking-unit">分钟</span>
                          </div>
                        </div>
                      </div>
                      <div className="ins-detail-modal__booking-footer">
                        <span className="ins-detail-modal__field-label">提前预约</span>
                        <span className="ins-detail-modal__booking-chip">
                          最多可提前 {detailItem.reservationRules?.advanceBookingDays ?? 7} 天
                        </span>
                      </div>
                      <div className="ins-detail-modal__booking-meta">
                        {detailItem.reservationRules?.requiresApproval === false
                          ? '预约无需审批（自动通过）'
                          : '预约需负责人审批'}
                        {typeof detailItem.reservationRules?.maxConcurrentUsers === 'number' && (
                          <> · 最多 {detailItem.reservationRules.maxConcurrentUsers} 人同时使用</>
                        )}
                      </div>
                    </div>
                  </section>
                </div>
              </div>
            </div>

            <footer className="ins-detail-modal__footer">
              <button
                type="button"
                className="ins-detail-modal__btn ins-detail-modal__btn--secondary"
                onClick={() => {
                  detailFetchSeq.current += 1;
                  setDetailLoading(false);
                  setShowDetail(false);
                }}
              >
                关闭
              </button>
              <button
                type="button"
                className="ins-detail-modal__btn ins-detail-modal__btn--primary"
                onClick={() => {
                  if (!detailItem) return;
                  setShowDetail(false);
                  handleBooking(detailItem);
                }}
              >
                <LimsNavIcon name="event_available" sizePx={18} style={{ fontVariationSettings: "'FILL' 1" }} />
                立即预约
              </button>
            </footer>

            {detailLoading && (
              <div className="ins-detail-modal__loading">
                <Spinner animation="border" size="sm" variant="primary" />
                <span>加载完整信息…</span>
              </div>
            )}
          </Modal.Body>
        )}
      </Modal>

      {/* 预约弹窗 */}
      <Modal 
        show={showBooking} 
        onHide={() => {
          if (bookingLoading) return;
          if (showBookingConfirm) {
            setShowBookingConfirm(false);
            return;
          }
          handleCloseBookingModal();
        }} 
        size={mobilePortrait ? undefined : "lg"}
        fullscreen={mobilePortrait ? true : undefined}
        style={mobilePortrait ? { margin: 0 } : undefined}
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: mobilePortrait ? '18px' : 'inherit' }}>
            预约使用 - {bookingInstrument?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: mobilePortrait ? '16px' : undefined, maxHeight: mobilePortrait ? 'calc(100vh - 120px)' : undefined, overflowY: mobilePortrait ? 'auto' : undefined }}>
          {alertMsg && (
            <Alert variant={alertType} className="mb-3">
              {alertMsg}
            </Alert>
          )}
          
          <Form>
            {/* 仪器信息 */}
            <div className="mb-4 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
              <h6 className="mb-2">仪器信息</h6>
              <div className="row">
                <div className="col-md-6">
                  <strong>仪器名称：</strong>{bookingInstrument?.name}
                </div>
                <div className="col-md-6">
                  <strong>型号：</strong>{bookingInstrument?.model || '-'}
                </div>
                <div className="col-md-6">
                  <strong>位置：</strong>{bookingInstrument?.location || '-'}
                </div>
                <div className="col-md-6">
                  <strong>负责人：</strong>{bookingInstrument?.responsiblePerson || '-'}
                </div>
              </div>
            </div>

            {/* 预约日期选择 - 日历组件 */}
            <Form.Group className="mb-3">
              <Form.Label style={{ fontSize: mobilePortrait ? '15px' : 'inherit', marginBottom: mobilePortrait ? '12px' : undefined }}>
                选择预约日期
              </Form.Label>
              <div style={{ 
                border: '1px solid #d9d9d9', 
                borderRadius: mobilePortrait ? '12px' : '6px', 
                padding: mobilePortrait ? '12px' : '8px' 
              }}>
                <Calendar
                  mode={calendarMode}
                  onSelect={handleCalendarDateSelect}
                  cellRender={renderCalendarCell}
                  fullscreen={!mobilePortrait}
                  disabledDate={(date) => {
                    if (!bookingInstrument) return true;
                    const rules = bookingInstrument.reservationRules;

                    const today = dayjs();
                    const targetDate = dayjs(date);

                    const advanceDays = rules?.advanceBookingDays || 7;
                    const maxDate = today.add(advanceDays, 'day');

                    if (targetDate.isBefore(today, 'day') || targetDate.isAfter(maxDate, 'day')) {
                      return true;
                    }

                    // 无规则对象、或未配置「按周几」的可预约时段时：不整月禁用（仅按提前预约天数）
                    const slots = rules?.availableTimeSlots;
                    const hasWeekdaySlotConfig =
                      Array.isArray(slots) &&
                      slots.length > 0 &&
                      slots.some((s) => s && typeof s.dayOfWeek === 'number');

                    if (!rules || !hasWeekdaySlotConfig) {
                      return false;
                    }

                    const dayOfWeek = date.day();
                    const timeSlot = slots.find((slot) => slot.dayOfWeek === dayOfWeek);
                    return !timeSlot || !timeSlot.isEnabled;
                  }}
                  onPanelChange={(date, mode) => setCalendarMode(mode)}
                />
              </div>
              {selectedCalendarDate && (
                <div className="mt-2">
                  <Tag color="blue">
                    已选择: {selectedCalendarDate.format('YYYY年MM月DD日 dddd')}
                  </Tag>
                </div>
              )}
            </Form.Group>

            {/* 时间段选择 */}
            {selectedDate && selectedCalendarDate && (
              <Form.Group className="mb-3">
                <Form.Label>选择预约时间段</Form.Label>
                <span className="mb-2 d-block">
                  {selectedStartTime && (
                    <span className="badge bg-primary me-2">
                      开始时间: {selectedStartTime}
                    </span>
                  )}
                  {selectedEndTime && (
                    <span className="badge bg-success">
                      结束时间: {selectedEndTime}
                    </span>
                  )}
                </span>
                
                {/* 显示当天的预约信息 */}
                {getReservationsForDate(selectedCalendarDate).length > 0 && (
                  <span className="mb-3 p-2 d-block" style={{ backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                    <span className="text-muted small d-block">当天已有预约：</span>
                    {getReservationsForDate(selectedCalendarDate).map((reservation, index) => (
                      <span key={index} className="mt-1 d-block">
                        <Tag color="orange">
                          {dayjs(reservation.startTime).format('HH:mm')} - {dayjs(reservation.endTime).format('HH:mm')}
                        </Tag>
                        <span className="ms-2 text-muted">
                          {(typeof reservation.userId === 'object' ? reservation.userId.name : '未知用户')} - {reservation.purpose}
                        </span>
                      </span>
                    ))}
                  </span>
                )}
                
                {/* 显示当天可预约时段范围 */}
                {bookingInstrument && (
                  <span className="mb-3 p-2 d-block" style={{ backgroundColor: '#e8f5e8', borderRadius: '4px' }}>
                    <span className="text-muted small d-block">当天可预约时段：</span>
                    <span className="mt-1 d-block">
                      {(() => {
                        const rules = bookingInstrument.reservationRules;
                        const dayOfWeek = selectedCalendarDate.day();
                        if (!rules || !rules.availableTimeSlots) {
                          return (
                            <Tag color="green">07:00 - 23:00</Tag>
                          );
                        }
                        const timeSlot = rules.availableTimeSlots.find(slot => slot.dayOfWeek === dayOfWeek);
                        if (!timeSlot || !timeSlot.isEnabled) {
                        return (
                            <Tag color="green">07:00 - 23:00</Tag>
                          );
                        }
                        return (
                          <Tag color="green">{timeSlot.startTime} - {timeSlot.endTime}</Tag>
                        );
                      })()}
                    </span>
                  </span>
                )}
                
                <span
                  className={`d-block is-booking-slot-grid${mobilePortrait ? ' is-booking-slot-grid--mobile' : ''}`}
                >
                  {timeSlots.map((slot, index) => {
                    const maxConcurrent = bookingInstrument?.reservationRules?.maxConcurrentUsers || 1;
                    const minStepMinutes = 30;
                    const dateStr = selectedCalendarDate.format('YYYY-MM-DD');
                    const { maxDuration } = bookingDurationLimits;
                    if (!slot.endTime) return null;

                    // 计算候选区间：未选择开始 -> 按最短时长段；已选择开始 -> 从选择的开始到该段结束
                    const rangeStart = selectedStartTime ? dayjs(`${dateStr}T${selectedStartTime}:00`) : dayjs(`${dateStr}T${slot.time}:00`);
                    const rangeEnd = selectedStartTime ? dayjs(`${dateStr}T${slot.endTime}:00`) : dayjs(`${dateStr}T${slot.endTime}:00`);

                    // 时长限制
                    const durationMinutes = rangeEnd.diff(rangeStart, 'minute');
                    const exceedsMaxDuration = durationMinutes > maxDuration;

                    // 容量扫描：以30分钟步长检查 [rangeStart, rangeEnd) 的并发数量
                    let cursor = rangeStart;
                    let exceedsCapacitySomewhere = false;
                    while (cursor.isBefore(rangeEnd)) {
                      const slotStart = cursor;
                      const stepEnd = cursor.add(minStepMinutes, 'minute');
                      const slotEnd = stepEnd.isBefore(rangeEnd) ? stepEnd : rangeEnd;
                      const concurrentAtSlot = occupyingReservations.filter(r => {
                        const rs = dayjs(r.startTime);
                        const re = dayjs(r.endTime);
                        // 允许边界相接
                        return rs.isBefore(slotEnd) && re.isAfter(slotStart);
                      }).length;
                      if (concurrentAtSlot >= maxConcurrent) {
                        exceedsCapacitySomewhere = true;
                        break;
                      }
                      cursor = slotEnd;
                    }

                    const isDisabled = slot.disabled || exceedsCapacitySomewhere || exceedsMaxDuration;
                    const isStartSelected = selectedStartTime && slot.time === selectedStartTime;
                    const label = `${slot.time}-${slot.endTime}`;

                    return (
                      <Tooltip
                        key={index}
                        title={
                          exceedsCapacitySomewhere
                            ? `该时间段内部分时段名额已满`
                            : exceedsMaxDuration
                              ? `超过可预约的最长时长`
                              : `可预约`
                        }
                      >
                        <Button
                          variant={isStartSelected ? 'primary' : (isDisabled ? 'danger' : 'outline-secondary')}
                          onClick={() => !isDisabled && handleTimeSlotClick(slot)}
                          disabled={isDisabled}
                          className="text-truncate"
                          style={{
                            fontSize: mobilePortrait ? '13px' : '14px',
                            padding: mobilePortrait ? '12px 6px' : '10px 8px',
                            minHeight: mobilePortrait ? '48px' : '42px',
                            fontWeight: 600
                          }}
                        >
                          {label}
                          {isDisabled && <span className="ms-1">🔒</span>}
                        </Button>
                      </Tooltip>
                    );
                  })}
                </span>
                <Form.Text className="text-muted">
                  点击一个30分钟段作为开始；再次点击更靠后的段作为结束。红色表示不可选（名额满或超过最长时长）。
                </Form.Text>
              </Form.Group>
            )}

            {/* 预约目的 */}
            <Form.Group className="mb-3">
              <Form.Label style={{ fontSize: mobilePortrait ? '15px' : 'inherit', marginBottom: mobilePortrait ? '12px' : undefined }}>
                预约目的 *
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={mobilePortrait ? 4 : 3}
                value={purpose}
                onChange={e => setPurpose(e.target.value)}
                placeholder="请详细说明预约使用该仪器的目的..."
                style={{ 
                  fontSize: mobilePortrait ? '16px' : 'inherit',
                  padding: mobilePortrait ? '12px' : undefined,
                  borderRadius: mobilePortrait ? '12px' : undefined
                }}
              />
            </Form.Group>

            {/* 预约规则提示 */}
            {bookingInstrument && (
              <div className="mb-3 p-3" style={{ backgroundColor: '#e3f2fd', borderRadius: '8px' }}>
                <h6 className="mb-2">预约规则</h6>
                <div className="row">
                  <div className="col-md-4">
                    <small>
                      <strong>最短时长：</strong>
                      {bookingDurationLimits.minDuration}分钟
                    </small>
                  </div>
                  <div className="col-md-4">
                    <small>
                      <strong>最长时长：</strong>
                      {bookingDurationLimits.maxDuration}分钟
                    </small>
                  </div>
                  <div className="col-md-4">
                    <small>
                      <strong>提前预约：</strong>最多
                      {bookingInstrument.reservationRules?.advanceBookingDays ?? 7}天
                    </small>
                  </div>
                </div>
                {Boolean(
                  bookingInstrument.responsiblePerson &&
                  bookingInstrument.responsiblePerson.trim() &&
                  bookingInstrument.reservationRules?.requiresApproval !== false
                ) && (
                  <div className="mt-2">
                    <small className="text-warning">
                      <strong>⚠️ 该仪器由设备负责人审批，预约后将进入待审核状态</strong>
                    </small>
                  </div>
                )}
                {!Boolean(
                  bookingInstrument.responsiblePerson &&
                  bookingInstrument.responsiblePerson.trim() &&
                  bookingInstrument.reservationRules?.requiresApproval !== false
                ) && (
                  <div className="mt-2">
                    <small className="text-success">
                      <strong>✓ 该仪器已关闭审批或未配置负责人，预约后将自动通过</strong>
                    </small>
                  </div>
                )}
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer style={{ 
          padding: mobilePortrait ? '12px 16px' : undefined,
          flexDirection: mobilePortrait ? 'column' : 'row',
          gap: mobilePortrait ? '10px' : '0'
        }}>
          <Button 
            variant="secondary" 
            onClick={handleCloseBookingModal}
            size={mobilePortrait ? "lg" : undefined}
            style={{ 
              width: mobilePortrait ? '100%' : 'auto',
              minHeight: mobilePortrait ? '44px' : 'auto',
              fontSize: mobilePortrait ? '16px' : 'inherit',
              order: mobilePortrait ? 2 : 1
            }}
          >
            取消
          </Button>
          <Button 
            variant="primary" 
            onClick={handleOpenBookingConfirm}
            disabled={bookingLoading || !selectedDate || !selectedStartTime || !selectedEndTime || !purpose.trim()}
            size={mobilePortrait ? "lg" : undefined}
            style={{ 
              width: mobilePortrait ? '100%' : 'auto',
              minHeight: mobilePortrait ? '44px' : 'auto',
              fontSize: mobilePortrait ? '16px' : 'inherit',
              order: mobilePortrait ? 1 : 2
            }}
          >
            提交预约
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showBookingConfirm}
        onHide={() => {
          if (!bookingLoading) setShowBookingConfirm(false);
        }}
        centered
        backdrop="static"
        keyboard={!bookingLoading}
      >
        <Modal.Header closeButton={!bookingLoading}>
          <Modal.Title>请确认预约信息</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3" style={{ fontSize: 15 }}>
            请核对以下信息。确认后将正式提交预约并占用所选时段；若有误请点击「返回修改」。
          </p>
          {bookingInstrument && selectedDate && (
            <dl className="row mb-0" style={{ fontSize: 15 }}>
              <dt className="col-sm-3 text-muted">仪器</dt>
              <dd className="col-sm-9 fw-semibold">{bookingInstrument.name}</dd>
              {bookingInstrument.location ? (
                <>
                  <dt className="col-sm-3 text-muted">位置</dt>
                  <dd className="col-sm-9">{bookingInstrument.location}</dd>
                </>
              ) : null}
              <dt className="col-sm-3 text-muted">使用日期</dt>
              <dd className="col-sm-9">
                {selectedCalendarDate
                  ? selectedCalendarDate.format('YYYY年MM月DD日 dddd')
                  : `${dayjs(selectedDate).format('YYYY年M月D日')}（${
                      ['周日', '周一', '周二', '周三', '周四', '周五', '周六'][dayjs(selectedDate).day()]
                    }）`}
              </dd>
              <dt className="col-sm-3 text-muted">时段</dt>
              <dd className="col-sm-9">
                {selectedStartTime} — {selectedEndTime}
                <span className="text-muted ms-2">
                  （约 {Math.max(0, parseTimeToMinutes(selectedEndTime) - parseTimeToMinutes(selectedStartTime))} 分钟）
                </span>
              </dd>
              <dt className="col-sm-3 text-muted">预约目的</dt>
              <dd className="col-sm-9" style={{ whiteSpace: 'pre-wrap' }}>
                {purpose.trim()}
              </dd>
              <dt className="col-sm-3 text-muted">后续状态</dt>
              <dd className="col-sm-9">
                {Boolean(
                  bookingInstrument.responsiblePerson?.trim() &&
                    bookingInstrument.reservationRules?.requiresApproval !== false
                ) ? (
                  <span className="text-warning">提交后需设备负责人审批（待审核），通过后方可按时段使用。</span>
                ) : (
                  <span className="text-success">该设备无需审批或已关闭审批，提交后将自动通过（已批准）。</span>
                )}
              </dd>
            </dl>
          )}
          {bookingInstrument ? (
            <div
              className="border rounded px-3 py-3 mt-3"
              style={{ backgroundColor: '#f8fafc', fontSize: 14, lineHeight: 1.55 }}
            >
              {(() => {
                const kb = parseInstrumentUsageKbRef(bookingInstrument);
                if (kb) {
                  return (
                    <>
                      <p className="mb-2 text-secondary">
                        若您不熟悉该仪器的操作，建议在提交前阅读知识库中的使用说明。
                      </p>
                      <Link
                        to={`/knowledge-base/${kb.id}`}
                        className="fw-semibold"
                      >
                        打开知识库使用说明{kb.title?.trim() ? `：${kb.title.trim()}` : ''}
                      </Link>
                    </>
                  );
                }
                return (
                  <p className="mb-0 text-secondary">
                    该仪器未在知识库中关联使用说明文档。若您不熟悉操作，请联系管理员获取指导。
                  </p>
                );
              })()}
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" disabled={bookingLoading} onClick={() => setShowBookingConfirm(false)}>
            返回修改
          </Button>
          <Button variant="primary" disabled={bookingLoading} onClick={() => void handleConfirmSubmitBooking()}>
            {bookingLoading ? '提交中…' : '确认提交'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showMyReservationsModal}
        onHide={() => setShowMyReservationsModal(false)}
        size={mobilePortrait ? undefined : 'xl'}
        fullscreen={mobilePortrait ? true : undefined}
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>我的预约</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: mobilePortrait ? undefined : 'min(80vh, 720px)', overflowY: 'auto' }}>
          {showMyReservationsModal ? (
            <MyInstrumentReservations key={myReservationsModalKey} />
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowMyReservationsModal(false)}>
            关闭
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 预约列表模态框 */}
      {selectedInstrumentForList && (
        <ReservationList
          instrumentId={selectedInstrumentForList._id!}
          instrumentName={selectedInstrumentForList.name}
          instrumentResponsiblePerson={selectedInstrumentForList.responsiblePerson}
          instrumentReservationRules={selectedInstrumentForList.reservationRules}
          onClose={() => {
            setSelectedInstrumentForList(null);
          }}
        />
      )}

      {selectedInstrumentForMaintenance?._id && (
        <InstrumentMaintenanceRecords
          instrumentId={selectedInstrumentForMaintenance._id}
          instrumentName={selectedInstrumentForMaintenance.name}
          onClose={() => setSelectedInstrumentForMaintenance(null)}
        />
      )}

      {showInfiniteSentinel && (
        <div
          ref={infiniteSentinelRef}
          className="is-infinite-sentinel d-flex flex-column align-items-center justify-content-center py-4"
          style={{ minHeight: 56 }}
          aria-busy={loadingMore}
        >
          {loadingMore ? (
            <>
              <Spinner animation="border" size="sm" variant="primary" />
              <span className="text-muted small mt-2">正在加载下一批…</span>
            </>
          ) : (
            <span className="text-muted small">继续下滑将加载更多设备</span>
          )}
        </div>
      )}
    </div>
  );
};

export default InstrumentShowcase;
